import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Grid, Box, Typography, Divider, Chip, IconButton, List, ListItem, ListItemIcon, ListItemText, ListItemSecondaryAction } from '@mui/material'
import { Edit, Print, PlayArrow, Check, Close, AttachFile, CloudUpload, Delete, Image, Description, InsertDriveFile } from '@mui/icons-material'
import { PageHeader, Button, Card, StatusChip, LoadingSpinner, Modal } from '@components/common'
import { toast } from 'react-toastify'
import api from '@services/api'

const WorkOrderDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const fileInputRef = useRef(null)
  const [workOrder, setWorkOrder] = useState(null)
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [attachments, setAttachments] = useState([])
  const [deleteAttachmentId, setDeleteAttachmentId] = useState(null)
  const [deleting, setDeleting] = useState(false)

  const fetchWorkOrder = async () => {
    try {
      const response = await api.get(`/maintenance/work-orders/${id}/`)
      setWorkOrder(response.data)
      setAttachments(response.data.attachment_files || [])
    } catch (error) {
      console.error('Error fetching work order:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchWorkOrder()
  }, [id])

  const handleFileUpload = async (event) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    setUploading(true)
    try {
      for (const file of files) {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('work_order', id)
        formData.append('file_name', file.name)
        formData.append('file_type', file.type.startsWith('image/') ? 'image' : 'document')
        formData.append('description', '')

        await api.post('/maintenance/work-order-attachments/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
      }
      toast.success('File(s) uploaded successfully')
      fetchWorkOrder()
    } catch (error) {
      console.error('Error uploading file:', error)
      toast.error('Failed to upload file')
    } finally {
      setUploading(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleDeleteAttachment = async () => {
    if (!deleteAttachmentId) return
    setDeleting(true)
    try {
      await api.delete(`/maintenance/work-order-attachments/${deleteAttachmentId}/`)
      toast.success('Attachment deleted')
      setAttachments(attachments.filter(a => a.id !== deleteAttachmentId))
      setDeleteAttachmentId(null)
    } catch (error) {
      console.error('Error deleting attachment:', error)
      toast.error('Failed to delete attachment')
    } finally {
      setDeleting(false)
    }
  }

  const getFileIcon = (fileType) => {
    if (fileType === 'image') return <Image color="primary" />
    if (fileType === 'document') return <Description color="info" />
    return <InsertDriveFile />
  }

  const formatFileSize = (bytes) => {
    if (!bytes) return '-'
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  if (loading) {
    return <LoadingSpinner message="Loading work order details..." />
  }

  if (!workOrder) {
    return (
      <Box textAlign="center" py={4}>
        <Typography>Work order not found</Typography>
        <Button onClick={() => navigate('/maintenance/work-orders')} sx={{ mt: 2 }}>
          Back to Work Orders
        </Button>
      </Box>
    )
  }

  const getStatusActions = () => {
    switch (workOrder.status) {
      case 'open':
        return (
          <Button startIcon={<PlayArrow />} color="info">
            Start Work
          </Button>
        )
      case 'in_progress':
        return (
          <Button startIcon={<Check />} color="success">
            Complete
          </Button>
        )
      case 'completed':
        return (
          <Button startIcon={<Close />} color="secondary">
            Close
          </Button>
        )
      default:
        return null
    }
  }

  return (
    <Box>
      <PageHeader
        title={workOrder.title}
        subtitle={`WO #${workOrder.work_order_number}`}
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Work Orders', path: '/maintenance/work-orders' },
          { label: workOrder.work_order_number },
        ]}
        actions={
          <>
            {getStatusActions()}
            <Button variant="outlined" startIcon={<Print />}>
              Print
            </Button>
            <Button variant="outlined" startIcon={<Edit />}>
              Edit
            </Button>
          </>
        }
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card title="Work Order Details">
            <Grid container spacing={2}>
              <Grid item xs={6} md={4}>
                <Typography variant="caption" color="text.secondary">
                  Status
                </Typography>
                <Box mt={0.5}>
                  <StatusChip status={workOrder.status} />
                </Box>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="caption" color="text.secondary">
                  Priority
                </Typography>
                <Box mt={0.5}>
                  <StatusChip status={workOrder.priority} />
                </Box>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="caption" color="text.secondary">
                  Category
                </Typography>
                <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                  {workOrder.category || '-'}
                </Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="caption" color="text.secondary">
                  Asset
                </Typography>
                <Typography variant="body2">
                  {workOrder.asset_name || '-'}
                </Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="caption" color="text.secondary">
                  Assigned To
                </Typography>
                <Typography variant="body2">
                  {workOrder.assigned_to_name || 'Unassigned'}
                </Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="caption" color="text.secondary">
                  Department
                </Typography>
                <Typography variant="body2">
                  {workOrder.department_name || '-'}
                </Typography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle2" gutterBottom>
              Description
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {workOrder.description || 'No description provided'}
            </Typography>

            {workOrder.resolution_description && (
              <>
                <Divider sx={{ my: 3 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Resolution
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {workOrder.resolution_description}
                </Typography>
              </>
            )}
          </Card>

          {/* Attachments Section */}
          <Card title="Attachments" sx={{ mt: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="body2" color="text.secondary">
                {attachments.length} file(s) attached
              </Typography>
              <input
                type="file"
                multiple
                ref={fileInputRef}
                onChange={handleFileUpload}
                style={{ display: 'none' }}
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
              />
              <Button
                startIcon={<CloudUpload />}
                onClick={() => fileInputRef.current?.click()}
                loading={uploading}
                size="small"
              >
                Upload Files
              </Button>
            </Box>

            {attachments.length === 0 ? (
              <Box textAlign="center" py={3} sx={{ bgcolor: 'grey.50', borderRadius: 1 }}>
                <AttachFile sx={{ fontSize: 40, color: 'grey.400' }} />
                <Typography variant="body2" color="text.secondary" mt={1}>
                  No attachments yet
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Upload images, documents, or reports
                </Typography>
              </Box>
            ) : (
              <List dense>
                {attachments.map((attachment) => (
                  <ListItem
                    key={attachment.id}
                    sx={{
                      bgcolor: 'grey.50',
                      borderRadius: 1,
                      mb: 1,
                      '&:hover': { bgcolor: 'grey.100' }
                    }}
                  >
                    <ListItemIcon>
                      {getFileIcon(attachment.file_type)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          component="a"
                          href={attachment.file_url || attachment.file}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ textDecoration: 'none', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
                        >
                          {attachment.file_name}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {formatFileSize(attachment.file_size)} | Uploaded by {attachment.uploaded_by_name || 'Unknown'} on {new Date(attachment.uploaded_at).toLocaleDateString()}
                        </Typography>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        size="small"
                        color="error"
                        onClick={() => setDeleteAttachmentId(attachment.id)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </Card>

          {/* Parts Used Section */}
          {workOrder.parts_used && workOrder.parts_used.length > 0 && (
            <Card title="Parts Used" sx={{ mt: 3 }}>
              <List dense>
                {workOrder.parts_used.map((part) => (
                  <ListItem key={part.id}>
                    <ListItemText
                      primary={part.spare_part_name}
                      secondary={`Part #: ${part.part_number} | Qty: ${part.quantity_used}`}
                    />
                    <Typography variant="body2">
                      {part.unit_cost ? `UGX ${Number(part.unit_cost * part.quantity_used).toLocaleString()}` : '-'}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            </Card>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          <Card title="Time & Cost">
            <Box display="flex" flexDirection="column" gap={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Estimated Hours
                </Typography>
                <Typography variant="body2">
                  {workOrder.estimated_hours || '-'} hrs
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Actual Hours
                </Typography>
                <Typography variant="body2">
                  {workOrder.actual_hours || '-'} hrs
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Downtime
                </Typography>
                <Typography variant="body2">
                  {workOrder.downtime_hours ? `${workOrder.downtime_hours} hrs` : '-'}
                </Typography>
              </Box>
            </Box>
          </Card>

          <Card title="Timeline" sx={{ mt: 3 }}>
            <Box display="flex" flexDirection="column" gap={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Created
                </Typography>
                <Typography variant="body2">
                  {new Date(workOrder.created_at).toLocaleString()}
                </Typography>
              </Box>
              {workOrder.start_time && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Started
                  </Typography>
                  <Typography variant="body2">
                    {new Date(workOrder.start_time).toLocaleString()}
                  </Typography>
                </Box>
              )}
              {workOrder.completion_time && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Completed
                  </Typography>
                  <Typography variant="body2">
                    {new Date(workOrder.completion_time).toLocaleString()}
                  </Typography>
                </Box>
              )}
            </Box>
          </Card>

          {/* Labor Records */}
          {workOrder.labor_records && workOrder.labor_records.length > 0 && (
            <Card title="Labor Records" sx={{ mt: 3 }}>
              <List dense>
                {workOrder.labor_records.map((labor) => (
                  <ListItem key={labor.id}>
                    <ListItemText
                      primary={labor.technician_name}
                      secondary={`${labor.hours_worked} hrs on ${new Date(labor.work_date).toLocaleDateString()}`}
                    />
                  </ListItem>
                ))}
              </List>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Delete Attachment Confirmation Modal */}
      <Modal
        open={!!deleteAttachmentId}
        onClose={() => setDeleteAttachmentId(null)}
        title="Delete Attachment"
        maxWidth="xs"
        actions={
          <>
            <Button variant="outlined" onClick={() => setDeleteAttachmentId(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button color="error" onClick={handleDeleteAttachment} loading={deleting}>
              Delete
            </Button>
          </>
        }
      >
        Are you sure you want to delete this attachment?
      </Modal>
    </Box>
  )
}

export default WorkOrderDetail
